/**
 * Formats an integer amount in minor units (e.g. US cents) as a localized currency string.
 * Never hand-format dollars — always go through this helper.
 */
export const formatMinor = (minor: number, currencyCode = 'USD', locale?: string): string => {
  const safeMinor = Number.isFinite(minor) ? minor : 0;
  return new Intl.NumberFormat(locale || undefined, {
    style: 'currency',
    currency: currencyCode || 'USD'
  }).format(safeMinor / 100);
};

/** Formats a signed amount with an explicit leading sign ("+$250.00" / "−$12.00"). */
export const formatSignedMinor = (minor: number, currencyCode = 'USD', locale?: string): string => {
  const formatted = formatMinor(Math.abs(minor), currencyCode, locale);
  if (minor < 0) return `−${formatted}`;
  if (minor > 0) return `+${formatted}`;
  return formatted;
};

/** Converts a user-entered major-unit amount ("2,500.50") to integer minor units. */
export const parseMajorToMinor = (input: string): number | null => {
  const cleaned = input.replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const value = parseFloat(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
};

/** The currency's symbol alone ("$", "CA$"), for prefixing an amount input. */
export const currencySymbol = (currencyCode = 'USD', locale?: string): string =>
  new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currencyCode || 'USD' })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? currencyCode;
